import { useCallback, useRef, useState } from 'react';
import { isMacPlatform } from '../../utils/keyboard';
import { copyModeKey, lastOutputRange, startCopyMode } from './copyModeModel';
import {
  applyCopySelection,
  bufferView,
  copyToClipboard,
  selectionText,
  isCopyChord,
  isCopyModeChord,
  lastOutputText,
} from './terminalCopy';

/**
 * Copy with the keyboard for one pane (Terminal board card 3): the copy
 * chords, copy mode and "Copy last output". `getTerm()` is the pane's xterm,
 * `getPrompts()` the buffer lines of its prompts (terminalPromptMarks).
 *
 * `onKey(e)` goes first in the pane's key filter: true means the key was
 * handled here and must not reach the shell. While copy mode is on, every key
 * is kept from the shell.
 */
export function usePaneCopy({ getTerm, getPrompts }) {
  const [copyMode, setCopyMode] = useState(false);
  const stateRef = useRef(null);
  const mac = isMacPlatform();

  const exitCopyMode = useCallback(() => {
    stateRef.current = null;
    setCopyMode(false);
    const term = getTerm();
    term?.clearSelection();
    term?.scrollToBottom();
    term?.focus();
  }, [getTerm]);

  const enterCopyMode = useCallback(() => {
    const term = getTerm();
    if (!term) return;
    const b = term.buffer.active;
    stateRef.current = startCopyMode(b.cursorX, b.baseY + b.cursorY);
    setCopyMode(true);
    applyCopySelection(term, stateRef.current);
    term.focus();
  }, [getTerm]);

  const copySelection = useCallback(() => copyToClipboard(selectionText(getTerm())), [getTerm]);

  const canCopyLastOutput = useCallback(() => !!lastOutputRange(getPrompts()), [getPrompts]);

  const copyLastOutput = useCallback(() => {
    const term = getTerm();
    return term ? copyToClipboard(lastOutputText(term, getPrompts())) : Promise.resolve(false);
  }, [getTerm, getPrompts]);

  const onKey = useCallback(
    (e) => {
      const term = getTerm();
      if (!term) return false;
      const keep = () => {
        e.preventDefault?.();
        e.stopPropagation?.();
        return true;
      };
      if (stateRef.current) {
        if (e.type !== 'keydown') return true;
        const { state, action } = copyModeKey(stateRef.current, e, bufferView(term));
        if (action === 'copy') {
          copyToClipboard(selectionText(term));
          exitCopyMode();
        } else if (action === 'exit') {
          exitCopyMode();
        } else if (action === 'consumed') {
          stateRef.current = state;
          applyCopySelection(term, state);
        }
        return keep();
      }
      if (isCopyModeChord(e, mac)) {
        enterCopyMode();
        return keep();
      }
      if (isCopyChord(e, mac, term.hasSelection())) {
        copySelection();
        // Windows Terminal clears the selection after a copy; macOS keeps it.
        if (!mac) term.clearSelection();
        return keep();
      }
      return false;
    },
    [getTerm, mac, enterCopyMode, exitCopyMode, copySelection]
  );

  return { copyMode, onKey, enterCopyMode, exitCopyMode, copySelection, copyLastOutput, canCopyLastOutput };
}
